import { createFileRoute, Link } from "../lib/router-compat";
import { PanBackdrop, YouTubeEmbed } from "../components/PageShell";

export const Route = createFileRoute("/classes")({
  head: () => ({
    meta: [
      { title: "Pan Institute Classes — The Pan Institute" },
      { name: "description", content: "Steel band classes for kids and adults — past and present ensembles." },
    ],
  }),
  component: ClassesPage,
});

const ENSEMBLES = [
  {
    name: "Rhythm Rockets",
    year: "Founded in 1992 by Nigel Chase",
    body: "",
    video: { id: "0CoKmpLT6dM", title: "Planet Pan: Bees Melody (2016)" },
  },
  {
    name: "Planet Pan Steel Band",
    year: "Founded in 1993 by Nigel and Carl Chase",
    body: "",
    video: { id: "PZipIzeKHMo", title: "Pan Coalition in Blue Hill, Maine — 7/8/2013" },
  },
  {
    name: "Flash in the Pans",
    year: "Founded in 1990 by Carl Chase, Butch Czerwinski, and Nigel Chase",
    body: "Community Adult Steel Band",
    video: { id: "E1PhhkqQbb4", title: "Flash in the Pans steel drum band" },
  },
  {
    name: "Pan Coalition",
    year: "Founded in 1998 by Nigel and Carl Chase",
    body: "A coalition of players of all ages and experience. Pan Coalition perform around the country and internationally.",
    video: { id: "iDMmHjSMDsk", title: "Pan Coalition — Outta De Box" },
  },
];

const PROGRAMS = [
  { who: "Ages 18+", level: "No experience", title: "Beginner band", body: "First notes, rhythm, listening, and ensemble basics. You'll be playing a tune by week three." },
  { who: "All ages", level: "Returning", title: "Continuing players", body: "Technique, repertoire, arranging, and performance prep with the full ensemble." },
  { who: "Ages 8–17", level: "All levels", title: "Kids programs", body: "Youth groups built around confidence, listening, and shared sound." },
  { who: "Schools & camps", level: "Short form", title: "Workshops", body: "Short-form sessions and residencies for schools, camps, and community groups." },
];

function ClassesPage() {
  return (
    <main className="min-h-screen text-foreground">
      <PanBackdrop />
      <section className="mx-auto max-w-6xl px-5 md:px-8 pt-28 md:pt-32 pb-16">
        {/* Header — top meta bar */}
        <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-2 text-[10px] uppercase tracking-[0.4em] text-foreground/55 border-b border-border/45 pb-4">
          <span>The Pan Institute · Brooksville, ME</span>
          <span className="text-primary">Fall semester · Now enrolling</span>
        </div>

        {/* Header — headline + ticket */}
        <div className="grid gap-10 md:grid-cols-[minmax(0,1fr)_300px] md:items-end pt-10 md:pt-14 pb-10">
          <div>
            <h1 className="font-display italic text-4xl sm:text-5xl md:text-6xl lg:text-[8.5rem] leading-[0.85] tracking-tight">
              Learn to Play Pan
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-foreground/70">
              Steel band classes offered for all ages. No experience required.
            </p>
          </div>

          <aside className="md:justify-self-end w-full md:w-[300px] border border-border/50 bg-card/30 backdrop-blur-sm p-6 md:p-7 relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-primary/80" />
            <div className="text-[10px] uppercase tracking-[0.4em] text-primary">Next intake</div>
            <div className="mt-4 flex items-baseline gap-3">
              <div className="font-display italic text-6xl leading-none">14</div>
              <div className="font-display italic text-2xl text-foreground/70 leading-none">Oct.</div>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-foreground/70">
              Brooksville Community Beginner Steel Band. Open to new players — no experience needed.
            </p>
            <Link
              to="/calendar"
              className="mt-6 inline-flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-primary hover:text-primary/70 transition group"
            >
              <span className="h-px w-8 bg-primary group-hover:w-12 transition-all" />
              Full schedule
            </Link>
          </aside>
        </div>

        {/* Programs — editorial numbered list */}
        <section className="border-t border-border/45 pt-10 md:pt-14 pb-10">
          <div className="grid gap-6 md:grid-cols-12 mb-10 items-end">
            <div className="md:col-span-7">
              <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">Programs</div>
              <h2 className="mt-4 font-display italic text-4xl md:text-6xl leading-[0.95]">
                Four ways<br /> into the band.
              </h2>
            </div>
            <p className="md:col-span-5 text-sm leading-relaxed text-foreground/60 md:pb-3">
            </p>
          </div>

          <ul className="border-t border-border/45">
            {PROGRAMS.map((p) => (
              <li
                key={p.title}
                className="group grid grid-cols-12 gap-x-4 gap-y-3 items-baseline border-b border-border/45 py-7 md:py-9 hover:bg-foreground/[0.02] transition"
              >
                <div className="col-span-12 md:col-span-6">
                  <h3 className="font-display italic text-3xl md:text-5xl leading-[0.98] group-hover:text-primary transition">
                    {p.title}
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-3 flex flex-col gap-1 text-[10px] uppercase tracking-[0.3em] text-foreground/75">
                  <span>{p.who}</span>
                  <span className="text-primary/85">{p.level}</span>
                </div>
                <p className="col-span-12 md:col-span-3 text-[14px] leading-relaxed text-foreground/80">
                  {p.body}
                </p>
              </li>
            ))}
          </ul>
        </section>

        <section className="pt-10 md:pt-14">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between border-b border-border/45 pb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">Student bands</div>
              <h2 className="mt-3 font-display italic text-4xl md:text-5xl leading-none">Past and present</h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-foreground/55">
              A tighter archive of the teaching program, with the video doing the work.
            </p>
          </div>

          <div className="divide-y divide-border/45">
            {ENSEMBLES.map((e) => (
              <article key={e.name} className="grid gap-5 md:grid-cols-[minmax(0,0.9fr)_minmax(320px,1.1fr)] md:items-center py-7 md:py-8">
                <div>
                  <h3 className="font-display italic text-3xl md:text-4xl leading-[1.02]">{e.name}</h3>
                  <div className="mt-3 text-[11px] uppercase tracking-[0.28em] text-primary/80">{e.year}</div>
                  {e.body && <p className="mt-4 max-w-md text-sm leading-relaxed text-foreground/65">{e.body}</p>}
                </div>
                <div className="md:max-w-[520px] md:justify-self-end w-full">
                  <YouTubeEmbed id={e.video.id} title={e.video.title} />
                </div>
              </article>
            ))}
          </div>
        </section>
      </section>
    </main>
  );
}
